import { computed } from 'vue'
import { withDomainHeaders } from '~/composable/useDomain'
import { buildLocalizedPath, normalizeLocale, stripLocalePrefix, type SupportedLocale } from '~/utils/localeController'
import { normalizeTenantLanguageOptions, resolveTenantMainLanguage } from '~/utils/tenantLanguage'

type RequestHeaderInput = Record<string, string | string[] | undefined>

type TenantSeoHeadLanguageItem = {
  code?: string | null
  name?: string | null
  label?: string | null
  emoji?: string | null
}

type TenantSeoHeadItem = {
  url?: string | null
  tenantId?: number | string | null
  seoText?: string | null
  pageTitle?: string | null
  metaDescription?: string | null
  metaContent?: string | null
  metaGtag?: string | null
  googleTagManager?: string | null
  mainLanguage?: string | null
  targetLanguage?: TenantSeoHeadLanguageItem[] | null
  targetLanguageCode?: string | null
  schemaOrg?: string | null
  faviconUrl?: string | null
  error?: string | null
}

type TenantSeoHeadResponse = {
  msg?: string
  code?: number
  data?: TenantSeoHeadItem | null
}

type UseTenantSeoHeadInput = {
  cacheKey: () => string
  candidateUrls: () => string[]
  locale: () => string
  reqHeaders?: RequestHeaderInput
  fallbackFaviconUrl?: () => string
  fallbackFaviconVersion?: () => string
}

const normalizeText = (value: unknown): string => String(value || '').trim()

const buildVersionedUrl = (url: string, version: string): string => {
  if (!url) return ''
  if (!version) return url
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`
}

const extractGoogleTagManagerId = (source: string): string => {
  return source.match(/GTM-[A-Z0-9]+/)?.[0] || ''
}

const normalizeSchemaOrg = (raw: unknown): string => {
  const source = normalizeText(raw)
  if (!source) return ''

  try {
    const parsed = JSON.parse(source)
    if (!parsed || typeof parsed !== 'object') return ''
    return JSON.stringify(parsed)
  } catch {
    return ''
  }
}

type AlternateLanguageLink = {
  key: string
  rel: 'alternate'
  hreflang: string
  href: string
}

const buildAlternateLanguageLinks = (
  canonicalUrl: string,
  targetLanguage: unknown,
  mainLanguage: unknown,
): AlternateLanguageLink[] => {
  if (!canonicalUrl) return []

  const configuredLanguages = normalizeTenantLanguageOptions(targetLanguage).map(item => item.code)
  if (!configuredLanguages.length) return []

  const resolvedMainLanguage = resolveTenantMainLanguage(mainLanguage, targetLanguage, configuredLanguages[0])
  const languages = Array.from(new Set<SupportedLocale>([
    normalizeLocale(resolvedMainLanguage),
    ...configuredLanguages,
  ]))

  try {
    const sourceUrl = new URL(canonicalUrl)
    const canonicalPath = stripLocalePrefix(sourceUrl.pathname).pathname
    const buildHref = (language: SupportedLocale) => {
      const localizedUrl = new URL(sourceUrl.href)
      localizedUrl.pathname = buildLocalizedPath(canonicalPath, language, resolvedMainLanguage)
      localizedUrl.hash = ''
      return localizedUrl.href
    }

    return [
      ...languages.map(language => ({
        key: `alternate-${language}`,
        rel: 'alternate' as const,
        hreflang: language,
        href: buildHref(language),
      })),
      {
        key: 'alternate-x-default',
        rel: 'alternate' as const,
        hreflang: 'x-default',
        href: buildHref(resolvedMainLanguage),
      },
    ]
  } catch {
    return []
  }
}

export const useTenantSeoHead = async (input: UseTenantSeoHeadInput) => {
  const { data: seoHeadConfig } = await useAsyncData<TenantSeoHeadItem | null>(
    () => input.cacheKey(),
    async () => {
      const candidates = Array.from(new Set(input.candidateUrls().map(normalizeText).filter(Boolean)))
      for (const url of candidates) {
        try {
          const resp = await $fetch<TenantSeoHeadResponse>('/api/biz/tenant/seoText/byFullUrl', {
            method: 'GET',
            query: { url },
            headers: withDomainHeaders({ ...(input.reqHeaders || {}), lang: input.locale() }),
          })
          if (resp?.data) return resp.data
        } catch {
          // ignore and try next URL candidate
        }
      }
      return null
    },
    {
      watch: [() => input.candidateUrls().join('|'), input.locale],
      default: () => null,
    },
  )

  const googleSiteVerificationContent = computed(() => normalizeText(seoHeadConfig.value?.metaContent))
  const gaMeasurementId = computed(() => normalizeText(seoHeadConfig.value?.metaGtag))
  const googleTagManagerId = computed(() => extractGoogleTagManagerId(normalizeText(seoHeadConfig.value?.googleTagManager)))
  const schemaOrgJson = computed(() => normalizeSchemaOrg(seoHeadConfig.value?.schemaOrg))
  const faviconUrl = computed(() => normalizeText(seoHeadConfig.value?.faviconUrl) || normalizeText(input.fallbackFaviconUrl?.()))
  const faviconVersion = computed(() => normalizeText(input.fallbackFaviconVersion?.()))
  const faviconHref = computed(() => buildVersionedUrl(faviconUrl.value, faviconVersion.value) || '/favicon.ico')
  const gaScriptSrc = computed(() => {
    if (!gaMeasurementId.value) return ''
    return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId.value)}`
  })
  const gaInlineScript = computed(() => {
    if (!gaMeasurementId.value) return ''
    return `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${gaMeasurementId.value}');`
  })
  const gtmScriptSrc = computed(() => {
    if (!googleTagManagerId.value) return ''
    return `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(googleTagManagerId.value)}`
  })
  const gtmInlineScript = computed(() => {
    if (!googleTagManagerId.value) return ''
    return `window.dataLayer = window.dataLayer || []; window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});`
  })
  const gtmNoScriptIframe = computed(() => {
    if (!googleTagManagerId.value) return ''
    return `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(googleTagManagerId.value)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
  })

  const tenantPageTitle = computed(() => normalizeText(seoHeadConfig.value?.pageTitle))
  const tenantMetaDescription = computed(() => normalizeText(seoHeadConfig.value?.metaDescription))
  const resolvedLang = computed(() => normalizeText(seoHeadConfig.value?.mainLanguage))

  type ApplyHeadOptions = {
    lang: () => string
    title?: () => string | undefined
    description?: () => string | undefined
    keywords?: () => string | undefined
    ogUrl?: () => string | undefined
    canonicalUrl?: () => string | undefined
  }

  const applyHead = (options: ApplyHeadOptions | (() => string)) => {
    const opts: ApplyHeadOptions = typeof options === 'function' ? { lang: options } : options

    useHead(() => {
      const resolvedTitle = opts.title?.() || tenantPageTitle.value || undefined
      const resolvedDescription = opts.description?.() || tenantMetaDescription.value || undefined
      const resolvedKeywords = opts.keywords?.() || undefined
      const resolvedOgUrl = opts.ogUrl?.() || undefined
      const resolvedCanonicalUrl = opts.canonicalUrl?.() || undefined
      const alternateLanguageLinks = buildAlternateLanguageLinks(
        resolvedCanonicalUrl || '',
        seoHeadConfig.value?.targetLanguage,
        seoHeadConfig.value?.mainLanguage,
      )

      return ({
        htmlAttrs: { lang: resolvedLang.value || opts.lang() },
        title: resolvedTitle,
        meta: [
          ...(googleSiteVerificationContent.value
            ? [{ name: 'google-site-verification', content: googleSiteVerificationContent.value }]
            : []),
          ...(resolvedTitle
            ? [{ property: 'og:title', content: resolvedTitle }]
            : []),
          ...(resolvedDescription
            ? [{ name: 'description', content: resolvedDescription }]
            : []),
          ...(resolvedDescription
            ? [{ property: 'og:description', content: resolvedDescription }]
            : []),
          ...(resolvedKeywords
            ? [{ name: 'keywords', content: resolvedKeywords }]
            : []),
          ...(resolvedOgUrl
            ? [{ property: 'og:url', content: resolvedOgUrl }]
            : []),
          ...(resolvedTitle
            ? [{ name: 'twitter:title', content: resolvedTitle }]
            : []),
          ...(resolvedDescription
            ? [{ name: 'twitter:description', content: resolvedDescription }]
            : []),
        ],
        script: [
          ...(gaMeasurementId.value
            ? [
                {
                  key: 'ga-script-src',
                  async: true,
                  src: gaScriptSrc.value,
                },
                {
                  key: 'ga-script-inline',
                  innerHTML: gaInlineScript.value,
                },
              ]
            : []),
          ...(googleTagManagerId.value
            ? [
                {
                  key: 'gtm-script-inline',
                  innerHTML: gtmInlineScript.value,
                },
                {
                  key: 'gtm-script-src',
                  async: true,
                  src: gtmScriptSrc.value,
                },
              ]
            : []),
          ...(schemaOrgJson.value
            ? [
                {
                  key: 'schema-org-jsonld',
                  type: 'application/ld+json',
                  innerHTML: schemaOrgJson.value,
                },
              ]
            : []),
        ],
        noscript: googleTagManagerId.value
          ? [
              {
                key: 'gtm-noscript',
                tagPosition: 'bodyOpen',
                innerHTML: gtmNoScriptIframe.value,
              },
            ]
          : [],
        link: [
          ...(resolvedCanonicalUrl
            ? [{ key: 'canonical', rel: 'canonical', href: resolvedCanonicalUrl }]
            : []),
          ...alternateLanguageLinks,
          { rel: 'icon', href: faviconHref.value },
          { rel: 'shortcut icon', href: faviconHref.value },
          { rel: 'apple-touch-icon', href: faviconHref.value },
        ],
      })
    })
  }

  return {
    seoHeadConfig,
    faviconHref,
    applyHead,
  }
}